import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Copy, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const PORTAL_URL = "https://udaycareeracademy.com/student-login";

export default function StudentCredentialsDialog({ open, onOpenChange, student, onSaved }) {
  const [email, setEmail] = useState(student?.login_email || student?.email || "");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedCreds, setSavedCreds] = useState(null); // {email, password} after save — enables copy buttons

  const buildMessage = (em, pw) =>
    `Uday Career Academy — Student Portal Login\n\nEmail: ${em}\nPassword: ${pw}\nPortal: ${PORTAL_URL}\n\nKeep these credentials confidential. You can also login using your phone number via OTP.`;

  const copyToClipboard = async (text, label) => {
    // Primary: use async Clipboard API (secure contexts)
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success(`${label} copied to clipboard`);
        return;
      }
    } catch { /* fall through to legacy */ }

    // Fallback: hidden textarea + execCommand (works in iframes / non-secure)
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "0";
      ta.style.left = "0";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, text.length);
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) {
        toast.success(`${label} copied to clipboard`);
        return;
      }
    } catch { /* ignore */ }

    toast.error("Auto-copy blocked. Select the message manually and press Ctrl+C.");
  };

  const openWhatsApp = (em, pw) => {
    const msg = encodeURIComponent(buildMessage(em, pw));
    const phone = (student?.phone || "").replace(/\D/g, "");
    // wa.me requires country code; assume Indian if 10 digits
    const waNumber = phone.length === 10 ? `91${phone}` : phone;
    const url = waNumber ? `https://wa.me/${waNumber}?text=${msg}` : `https://wa.me/?text=${msg}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const save = async () => {
    if (!email || password.length < 6) {
      return toast.error("Email required. Password must be at least 6 characters.");
    }
    setSaving(true);
    try {
      const cleanEmail = email.trim().toLowerCase();
      await api.post(`/students/${student.id}/credentials`, { email: cleanEmail, password });
      toast.success("Login credentials saved");
      setSavedCreds({ email: cleanEmail, password });
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save credentials");
    } finally {
      setSaving(false);
    }
  };

  const close = () => {
    setSavedCreds(null);
    setPassword("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setSavedCreds(null); setPassword(""); } onOpenChange(v); }}>
      <DialogContent data-testid="student-credentials-dialog">
        <DialogHeader>
          <DialogTitle>Set Login Credentials · {student?.name}</DialogTitle>
        </DialogHeader>

        {!savedCreds ? (
          <>
            <div className="space-y-4">
              <p className="text-sm text-slate-600 p-3 bg-slate-50 border border-slate-200 rounded-md">
                Set an email and password so this student can sign in via the <strong>Email</strong> tab on the student login page. Phone OTP login will continue to work in parallel.
              </p>
              <div>
                <Label className="label-caps">Login Email</Label>
                <Input
                  data-testid="cred-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 h-11"
                  placeholder="student@example.com"
                  autoComplete="off"
                />
              </div>
              <div>
                <Label className="label-caps">Password (min 6 chars)</Label>
                <Input
                  data-testid="cred-password"
                  type="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 h-11 font-mono"
                  placeholder="temp1234"
                  autoComplete="new-password"
                />
                <p className="text-xs text-slate-500 mt-1">Shown as plain text so you can copy and share with the student.</p>
              </div>
              {student?.login_email && (
                <div className="text-xs p-2 bg-emerald-50 border border-emerald-200 rounded text-emerald-800">
                  Existing login email: <span className="font-mono font-semibold">{student.login_email}</span> — saving will overwrite.
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button className="bg-slate-900 hover:bg-slate-800" onClick={save} disabled={saving} data-testid="save-credentials">
                {saving ? "Saving…" : "Save Credentials"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-4">
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md text-emerald-800 text-sm">
                ✅ Credentials saved. Share them with the student below.
              </div>
              <div className="bg-slate-900 text-slate-100 rounded-md p-4 font-mono text-sm whitespace-pre-wrap" data-testid="creds-preview">
{buildMessage(savedCreds.email, savedCreds.password)}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Button
                  variant="outline"
                  onClick={() => copyToClipboard(buildMessage(savedCreds.email, savedCreds.password), "Message")}
                  data-testid="copy-message"
                  className="h-11"
                >
                  <Copy size={14} className="mr-2" /> Copy Message
                </Button>
                <Button
                  variant="outline"
                  onClick={() => copyToClipboard(savedCreds.password, "Password")}
                  data-testid="copy-password"
                  className="h-11"
                >
                  <Copy size={14} className="mr-2" /> Copy Password
                </Button>
                <Button
                  onClick={() => openWhatsApp(savedCreds.email, savedCreds.password)}
                  data-testid="send-whatsapp"
                  className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <MessageCircle size={14} className="mr-2" /> Send on WhatsApp
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                WhatsApp button opens WhatsApp Web / app with the message pre-filled to the student&apos;s registered number ({student?.phone || "no number"}).
              </p>
            </div>
            <DialogFooter>
              <Button className="bg-slate-900 hover:bg-slate-800" onClick={close} data-testid="close-credentials">Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
